import { FmcsaLookup } from "@/components/FmcsaLookup";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useList, useUsersMap, useInvalidate } from "@/lib/hooks";
import api, { apiError } from "@/lib/api";
import { Panel, EmptyState, Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import {
  Plus, Users, MagnifyingGlass, CaretRight, Trash, PaperPlaneTilt, Funnel,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { LEAD_STATUSES, LEAD_PIPELINE, AUTO_STAGES, DISPATCH_PLANS, leadEmpty, TRUCK_TYPES, isLeadStuck, leadStuckDays } from "@/lib/leadConfig";
import { LeadStatusPill, OnboardingTypeChip, RiskRejectDialog } from "@/components/LeadStatus";

const STATUSES = LEAD_STATUSES;
const empty = leadEmpty;


function FunnelView({ funnel, onPick }) {
  const counts = funnel?.counts || {};
  const max = Math.max(1, ...LEAD_PIPELINE.map((s) => counts[s.key] || 0));
  return (
    <Panel className="p-5 mb-4">
      <div className="flex items-center justify-between mb-3"><p className="text-xs font-bold uppercase tracking-wide text-slate-400 flex items-center gap-1.5"><Funnel size={14} weight="fill" />Pipeline Funnel</p><button onClick={() => onPick("Risk Rejected")} className="text-xs font-semibold text-red-600 hover:text-red-700" data-testid="funnel-rejected">Risk Rejected · {counts["Risk Rejected"] || 0}</button></div>
      <div className="space-y-2">
        {LEAD_PIPELINE.map((s, i) => {
          const c = counts[s.key] || 0;
          const pct = Math.round((c / max) * 100);
          const shade = 92 - i * 9;
          return (
            <button key={s.key} onClick={() => onPick(s.key)} data-testid={`funnel-${i}`} className="w-full flex items-center gap-3 group">
              <span className="w-28 text-xs font-medium text-slate-600 text-right shrink-0 truncate">{s.short}</span>
              <div className="flex-1 h-7 rounded-md bg-slate-100 overflow-hidden relative">
                <div className="h-full rounded-md transition-all group-hover:brightness-95 flex items-center px-2" style={{ width: `${Math.max(pct, c > 0 ? 8 : 0)}%`, background: `hsl(217 91% ${shade}%)`, minWidth: c > 0 ? 28 : 0 }}>
                  {c > 0 && <span className="text-xs font-bold text-white tabular">{c}</span>}
                </div>
                {c === 0 && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 tabular">0</span>}
              </div>
            </button>
          );
        })}
      </div>
    </Panel>
  );
}

export default function Leads() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSales = user.role === "sales";
  const isAdmin = user.role === "admin";
  const invalidate = useInvalidate();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const { data: leads = [], isLoading } = useList(["leads", status, search], `/leads?status=${status}&q=${encodeURIComponent(search)}`);
  const { data: funnel } = useList("leads-funnel", "/leads/funnel");
  const { users } = useUsersMap();
  const salesAgents = users.filter((u) => u.role === "sales" || u.role === "admin");

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const [packetOpen, setPacketOpen] = useState(false);
  const [packet, setPacket] = useState({ carrier_name: "", owner_name: "", email: "", phone: "", lead_id: "", plan_percent: "" });
  const [riskLead, setRiskLead] = useState(null);
  const [sending, setSending] = useState(false);

  const openNew = () => { setForm(empty); setCreating(true); };

  const createLead = async () => {
    if (!form.company) { toast.error("Company name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, estimated_value: Number(form.estimated_value) || 0, num_trucks: Number(form.num_trucks) || 0, expected_gross_weekly: Number(form.expected_gross_weekly) || 0, conversion_possibility: Number(form.conversion_possibility) || 0, assigned_to: form.assigned_to || null };
      await api.post("/leads", payload);
      toast.success("Lead created");
      setCreating(false);
      invalidate(["leads", "dashboard", "leads-funnel"]);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const putStatus = async (l, body) => {
    try { await api.put(`/leads/${l.id}`, body); toast.success(`Stage → ${body.status}`); invalidate(["leads", "dashboard", "leads-funnel"]); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); throw e; }
  };
  const changeStatus = (l, newStatus) => {
    if (newStatus === "Risk Rejected") { setRiskLead(l); return; }
    putStatus(l, { status: newStatus }).catch(() => {});
  };
  const pickLeadForPacket = (id) => {
    const l = leads.find((x) => x.id === id);
    setPacket(l ? { carrier_name: l.company || "", owner_name: l.owner_manager || l.contact_name || "", email: l.email || "", phone: l.phone || "", lead_id: l.id, plan_percent: packet.plan_percent } : { ...packet, lead_id: "" });
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/leads/${id}`);
    invalidate(["leads", "dashboard", "leads-funnel"]);
    toast.success("Lead deleted");
  };

  const sendPacket = async () => {
    if (!packet.carrier_name.trim() || !packet.email.trim()) { toast.error("Carrier name and email are required"); return; }
    setSending(true);
    try {
      if (!packet.plan_percent) { toast.error("Select the dispatch plan discussed"); setSending(false); return; }
      const { data } = await api.post("/carrier-packets", { ...packet, plan_percent: Number(packet.plan_percent), lead_id: packet.lead_id || null, base_url: window.location.origin });
      invalidate(["leads", "leads-funnel"]);
      if (data.email_sent) toast.success("Carrier packet emailed successfully"); else if (data.email_configured) toast.warning("Packet created, but the email failed to send — share the link manually"); else toast.success("Packet created (email not configured)");
      setPacketOpen(false);
      setPacket({ carrier_name: "", owner_name: "", email: "", phone: "", lead_id: "", plan_percent: "" });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const inp = "bg-white border-slate-300";

  return (
    <div className="max-w-[1500px] mx-auto">
      <div className="relative overflow-hidden rounded-3xl bg-[#07090f] border border-[#1f2937] px-6 sm:px-8 py-7 mb-5 animate-fade-up" data-testid="leads-hero">
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 88% 15%, rgba(37,99,235,.45), transparent 42%), radial-gradient(circle at 5% 100%, rgba(245,158,11,.25), transparent 40%)" }} />
        <div className="relative flex flex-col lg:flex-row lg:items-end justify-between gap-5">
          <div><p className="text-[11px] font-bold uppercase tracking-[0.28em] text-blue-400">Sales pipeline</p>
            <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-extrabold text-white">Lead Management</h1>
            <p className="mt-2 text-sm text-slate-400">{isSales ? "Your assigned leads, from first call to dispatch-ready." : "Track and convert every carrier lead across your pipeline."}</p></div>
          <div className="flex flex-wrap items-center gap-3">
            {[["Total", funnel?.total ?? "—"], ["In pipeline", funnel ? funnel.total - funnel.rejected : "—"], ["Conversion", funnel ? `${funnel.conversion_rate}%` : "—"], ["Rejected", funnel?.rejected ?? "—"]].map(([l, v]) => (
              <div key={l} className="rounded-2xl bg-white/5 border border-white/10 px-4 py-2.5 min-w-[96px]" data-testid={`leads-hero-${l.toLowerCase().replace(/\s+/g, "-")}`}><p className="font-heading text-xl font-extrabold text-white tabular">{v}</p><p className="text-[10px] uppercase tracking-wider text-slate-400">{l}</p></div>))}
            <Button data-testid="carrier-packet-btn" onClick={() => setPacketOpen(true)} className="h-11 bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold">
              <PaperPlaneTilt size={17} weight="fill" className="mr-1.5" />Send Carrier Packet
            </Button>
          </div>
        </div>
      </div>

      <FunnelView funnel={funnel} onPick={setStatus} />

      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="lead-quick-status">
        {["all", ...STATUSES.slice(0, 8)].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`h-8 px-3 rounded-full text-xs font-semibold transition-colors ${status === s ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"}`} data-testid={`lead-chip-${s}`}>{s === "all" ? "All" : s}{s !== "all" && funnel?.counts?.[s] ? ` · ${funnel.counts[s]}` : ""}</button>))}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input data-testid="lead-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search carrier, contact…" className="pl-9 bg-white border-slate-200" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full sm:w-56 bg-white border-slate-200" data-testid="lead-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Panel className="p-0">
        {isLoading ? <div className="p-6 text-slate-500">Loading…</div> : leads.length === 0 ? (
          <EmptyState icon={Users} title="No leads found" hint="Leads appear here once Sales submits them via Submit Lead." />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-200 hover:bg-transparent">
                  <TableHead>Lead ID</TableHead>
                  <TableHead>Carrier Name</TableHead>
                  <TableHead className="hidden md:table-cell">Phone</TableHead>
                  <TableHead className="hidden lg:table-cell">MC</TableHead>
                  <TableHead className="hidden lg:table-cell">USDOT</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((l) => (
                  <TableRow key={l.id} className="border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors duration-150 animate-fade-up" data-testid={`lead-row-${l.id}`} onClick={() => navigate(`/leads/${l.id}`)}>
                    <TableCell className="font-mono font-semibold text-blue-600" data-testid={`lead-open-${l.id}`}>#{l.lead_number || "—"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900">{l.company}</p>
                        <OnboardingTypeChip type={l.onboarding_type} />
                        {isLeadStuck(l) && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-red-600 bg-red-50 px-1.5 py-0.5 rounded-full ring-1 ring-red-200" data-testid={`stuck-${l.id}`}>⚠ Stuck {leadStuckDays(l)}d</span>}
                      </div>
                      <p className="text-xs text-slate-500">{l.owner_manager || l.contact_name || "—"}{l.authorized_by_name && <span className="ml-2 text-emerald-600 font-semibold" data-testid={`lead-approved-by-${l.id}`}>✓ Approved by {l.authorized_by_name}</span>}</p>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-slate-600 font-mono">{l.phone || "—"}</TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-slate-600 font-mono">{l.mc_number || "—"}</TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-slate-600 font-mono">{l.usdot || "—"}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Select value={l.status} onValueChange={(v) => changeStatus(l, v)}>
                        <SelectTrigger className="h-7 w-auto border-0 bg-transparent p-0 gap-1 shadow-none" data-testid={`lead-status-${l.id}`}><LeadStatusPill status={l.status} /></SelectTrigger>
                        <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} disabled={AUTO_STAGES.includes(s) && !isAdmin}>{s}{AUTO_STAGES.includes(s) ? " (Auto)" : ""}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        {user.role === "admin" && <button onClick={() => remove(l.id)} className="p-1.5 text-slate-400 hover:text-red-400" data-testid={`delete-lead-${l.id}`}><Trash size={16} /></button>}
                        <button onClick={() => navigate(`/leads/${l.id}`)} className="p-1.5 text-slate-400 hover:text-blue-600" data-testid={`view-lead-${l.id}`}><CaretRight size={18} weight="bold" /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Panel>

      {/* NEW LEAD */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="bg-white border-slate-200 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">New Lead</DialogTitle><DialogDescription className="sr-only">Create a new carrier lead</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <div className="sm:col-span-2"><FmcsaLookup testid="lead-fmcsa" onSelect={(r) => setForm((f) => ({ ...f, company: r.legal_name || f.company, email: r.email || f.email, phone: r.phone || f.phone, mc_number: r.mc_number || f.mc_number, usdot: r.usdot || f.usdot, address: r.address || f.address, num_trucks: r.power_units || f.num_trucks }))} /></div>
            <Field label="Trucking Company *"><Input data-testid="lead-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={inp} /></Field>
            <Field label="Owner / Manager"><Input data-testid="lead-owner" value={form.owner_manager} onChange={(e) => setForm({ ...form, owner_manager: e.target.value })} className={inp} /></Field>
            <Field label="Email"><Input data-testid="lead-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} /></Field>
            <Field label="Phone"><Input data-testid="lead-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inp} /></Field>
            <Field label="MC #"><Input data-testid="lead-mc" value={form.mc_number} onChange={(e) => setForm({ ...form, mc_number: e.target.value })} className={`${inp} font-mono`} /></Field>
            <Field label="USDOT #"><Input data-testid="lead-usdot" value={form.usdot} onChange={(e) => setForm({ ...form, usdot: e.target.value })} className={`${inp} font-mono`} /></Field>
            <div className="sm:col-span-2"><Field label="Address"><Input data-testid="lead-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inp} /></Field></div>
            <div className="sm:col-span-2">
              <Field label="Type of Truck">
                <div className="flex flex-wrap gap-2">
                  {TRUCK_TYPES.map((t) => {
                    const on = (form.truck_types || []).includes(t);
                    return <button type="button" key={t} onClick={() => setForm({ ...form, truck_types: on ? form.truck_types.filter((x) => x !== t) : [...(form.truck_types || []), t] })} className={`px-2.5 py-1 rounded-full text-sm font-medium border transition-colors ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-300 hover:border-blue-400"}`}>{t}</button>;
                  })}
                </div>
              </Field>
            </div>
            <Field label="No. of Trucks"><Input data-testid="lead-num-trucks" type="number" value={form.num_trucks} onChange={(e) => setForm({ ...form, num_trucks: e.target.value })} className={`${inp} font-mono`} /></Field>
            {user.role === "admin" && <Field label="Estimated Value ($)"><Input type="number" value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} className={`${inp} font-mono`} /></Field>}
            {user.role === "admin" && (
              <Field label="Assign to Agent">
                <Select value={form.assigned_to || "none"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "none" ? "" : v })}>
                  <SelectTrigger className={inp}><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {salesAgents.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            )}
            <div className="sm:col-span-2"><Field label="Notes"><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inp} rows={3} /></Field></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)} className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50">Cancel</Button>
            <Button data-testid="save-lead-btn" onClick={createLead} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold">{saving ? "Saving…" : "Create Lead"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SEND CARRIER PACKET */}
      <RiskRejectDialog lead={riskLead} onClose={() => setRiskLead(null)} onSubmit={(body) => putStatus(riskLead, body)} />

      <Dialog open={packetOpen} onOpenChange={setPacketOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><PaperPlaneTilt size={20} weight="fill" className="text-emerald-500" />Send Carrier Packet</DialogTitle><DialogDescription className="sr-only">Email a carrier an onboarding link</DialogDescription></DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">We'll email the carrier a secure onboarding link to complete their packet.</p>
          <div className="space-y-4 py-2">
            <Field label="Link to Lead (sets stage to OPI)">
              <Select value={packet.lead_id || "none"} onValueChange={(v) => pickLeadForPacket(v === "none" ? "" : v)}>
                <SelectTrigger className={inp} data-testid="packet-lead-select"><SelectValue placeholder="Select a lead" /></SelectTrigger>
                <SelectContent><SelectItem value="none">No lead (new carrier)</SelectItem>{leads.filter((l) => ["Qualified", "Follow-up", "Sent for Final Approval", "Approved"].includes(l.status)).map((l) => <SelectItem key={l.id} value={l.id}>#{l.lead_number} · {l.company}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Dispatch Charges Plan Discussed *">
              <div className="flex flex-wrap gap-2" data-testid="packet-plan">{DISPATCH_PLANS.map((p) => (
                <button key={p} type="button" onClick={() => setPacket({ ...packet, plan_percent: p })} data-testid={`packet-plan-${p}`}
                  className={`h-9 px-4 rounded-full border text-sm font-semibold transition-colors ${Number(packet.plan_percent) === p ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-700 hover:border-slate-500"}`}>{p.toFixed(2).replace(/\.?0+$/, "")}%</button>
              ))}</div>
            </Field>
            <Field label="Carrier Name *"><Input data-testid="packet-carrier" value={packet.carrier_name} onChange={(e) => setPacket({ ...packet, carrier_name: e.target.value })} className={inp} /></Field>
            <Field label="Owner Name"><Input data-testid="packet-owner" value={packet.owner_name} onChange={(e) => setPacket({ ...packet, owner_name: e.target.value })} className={inp} /></Field>
            <Field label="Email *"><Input data-testid="packet-email" value={packet.email} onChange={(e) => setPacket({ ...packet, email: e.target.value })} className={inp} type="email" /></Field>
            <Field label="Phone Number"><Input data-testid="packet-phone" value={packet.phone} onChange={(e) => setPacket({ ...packet, phone: e.target.value })} className={inp} /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPacketOpen(false)} className="border-slate-300 bg-white text-slate-700">Cancel</Button>
            <Button onClick={sendPacket} disabled={sending} className="bg-emerald-600 text-white hover:bg-emerald-700 font-semibold" data-testid="packet-send-btn">{sending ? "Sending…" : "Send Packet"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
